'use client';

/**
 * The store's product rail, in the planner (ca_preview only). Same card as
 * the store's homepage and product-page rails: warm stage tile, brand,
 * shopper name, price, the red "Save $" pill when there is a real
 * compare-at saving, and the product page's one-tap "+ Add" (items with
 * options link through to choose, as the store does). Adds go to the real
 * Shopify cart (same origin) and bump the header's live count.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useCaPreview } from '../../lib/caPreview';
import { GEAR_HANDLES, money, toGearItem, type GearContext, type GearItem } from '../../lib/premiumGear';
import { addToStoreCart, storeUrl } from '../../lib/storeCart';
import SNAPSHOT from '../../lib/gearSnapshot.json';

type Snap = { title: string; vendor: string; image: string };

/**
 * First paint from the snapshot (name, brand, pack-shot), so a rail never
 * shows empty tiles while the store answers. Price, saving, stock and the
 * Add button are only ever shown from the live store response.
 */
function snapshotItem(handle: string): GearItem | null {
  const s = (SNAPSHOT as Record<string, Snap>)[handle];
  if (!s) return null;
  return toGearItem({ handle, title: s.title, vendor: s.vendor, featured_image: s.image, price: 0, variants: [], available: true });
}

const cache = new Map<string, Promise<GearItem | null>>();
function loadItem(handle: string): Promise<GearItem | null> {
  let p = cache.get(handle);
  if (!p) {
    p = fetch(storeUrl(`/products/${encodeURIComponent(handle)}.js`), { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => toGearItem(j))
      .catch(() => null);
    cache.set(handle, p);
  }
  return p;
}

/** Start a rail's store fetches before it mounts (ca_preview pages call this early). */
export function prefetchGear(context: GearContext) {
  GEAR_HANDLES[context].forEach((h) => { void loadItem(h); });
}

function QuickAdd({ item }: { item: GearItem }) {
  const [state, setState] = useState<'idle' | 'adding' | 'added' | 'error'>('idle');
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (t.current) clearTimeout(t.current); }, []);
  if (!item.singleVariant || !item.available) {
    return (
      <a className="ca-sg__add ca-sg__add--link" href={storeUrl(`/products/${item.handle}`)} tabIndex={-1} aria-hidden="true">
        {item.available ? 'Choose options' : 'View'}
      </a>
    );
  }
  const onClick = async () => {
    if (state === 'adding') return;
    setState('adding');
    try {
      await addToStoreCart({ handle: item.handle, variantId: item.variantId, title: item.title, image: item.image });
      setState('added');
      t.current = setTimeout(() => setState('idle'), 4000);
    } catch {
      setState('error');
      t.current = setTimeout(() => setState('idle'), 2600);
    }
  };
  return (
    <button
      type="button"
      className={`ca-sg__add${state === 'added' ? ' is-added' : ''}`}
      onClick={onClick}
      disabled={state === 'adding'}
      aria-busy={state === 'adding'}
      aria-label={`Add ${item.title} to cart`}
      data-testid="gear-quick-add"
    >
      {state === 'added' ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      )}
      <span>{state === 'adding' ? 'Adding' : state === 'added' ? 'Added' : state === 'error' ? 'Try again' : 'Add'}</span>
    </button>
  );
}

export default function StoreGearRail({
  context,
  heading,
  lede,
  linkHref = '/collections/all',
  linkLabel = 'Shop all gear',
  limit = 8,
  className = '',
  id,
}: {
  context: GearContext;
  heading: string;
  lede?: string;
  linkHref?: string;
  linkLabel?: string;
  limit?: number;
  className?: string;
  id?: string;
}) {
  const preview = useCaPreview();
  // handle -> live item (null: gone or sold out). No key yet: still loading.
  const [live, setLive] = useState<Record<string, GearItem | null>>({});
  useEffect(() => {
    if (!preview) return;
    let on = true;
    GEAR_HANDLES[context].forEach((h) =>
      loadItem(h).then((it) => {
        if (on) setLive((m) => ({ ...m, [h]: it && it.available ? it : null }));
      }),
    );
    return () => { on = false; };
  }, [preview, context]);

  const rows: { item: GearItem; ready: boolean }[] = [];
  for (const h of GEAR_HANDLES[context]) {
    if (rows.length >= limit) break;
    if (h in live) {
      const it = live[h];
      if (it) rows.push({ item: it, ready: true });
    } else {
      const it = snapshotItem(h);
      if (it) rows.push({ item: it, ready: false });
    }
  }

  if (!preview || rows.length === 0) return null;

  return (
    <section className={`ca-sg ${className}`} aria-labelledby={id ? `${id}-h` : undefined} id={id}>
      <div className="ca-sg__head">
        <div>
          <h2 id={id ? `${id}-h` : undefined} className="ca-sg__h">{heading}</h2>
          {lede && <p className="ca-sg__lede">{lede}</p>}
        </div>
        <a className="ca-sg__link" href={storeUrl(linkHref)}>
          {linkLabel}
          <span aria-hidden="true"> →</span>
        </a>
      </div>
      <ul className="ca-sg__track" role="list">
        {rows.map(({ item: it, ready }) => (
            <li key={it.handle} className={`ca-sg__card${ready ? '' : ' is-pending'}`}>
              <a className="ca-sg__a" href={storeUrl(`/products/${it.handle}`)}>
                <span className="ca-sg__img">
                  {/* Eager, from the snapshot on first paint: a store grid
                      never shows blank tiles on a fast scroll. */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={`${it.image}${it.image && it.image.includes('?') ? '&' : '?'}width=480`} alt={it.title} loading="eager" decoding="async" />
                  {ready && it.compareAt && <span className="ca-sg__save">Save {money(it.compareAt - it.price)}</span>}
                </span>
                <span className="ca-sg__vendor">{it.vendor}</span>
                <span className="ca-sg__t">{it.title}</span>
                {ready ? (
                <span className="ca-sg__price">
                  {it.priceVaries && <span className="ca-sg__from">From</span>}
                  <span className={`ca-sg__now${it.compareAt ? ' is-sale' : ''}`}>{money(it.price)}</span>
                  {it.compareAt && (
                    <s className="ca-sg__was">
                      <span className="ca-visually-hidden">Was </span>
                      {money(it.compareAt)}
                    </s>
                  )}
                </span>
                ) : (
                  <span className="ca-sg__price ca-sg__price--wait" aria-hidden="true">&nbsp;</span>
                )}
              </a>
              {ready ? <QuickAdd item={it} /> : <span className="ca-sg__add ca-sg__add--wait" aria-hidden="true" />}
            </li>
        ))}
      </ul>
    </section>
  );
}
